import * as React from 'react';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import MuiCard from '@mui/material/Card';
import AppTheme from '../shared-theme/AppTheme';
import CssBaseline from '@mui/material/CssBaseline';
import { styled } from '@mui/material/styles';

interface LoadingProgressProps {
  open: boolean;
  loadedTo: string;
  progress: number;
  meterType?: number;
  onComplete?: () => void;
  tariffName?: string;
  agreementPeriod?: string;
  showAgreementDetails?: boolean;
}

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  ...theme.applyStyles('dark', {
    background: 'hsla(220, 30%, 3%, 1)',
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

export default function LoadingProgress({open, loadedTo, progress, meterType, onComplete, tariffName, agreementPeriod, showAgreementDetails}: LoadingProgressProps) {
  const [showPatienceMessage, setShowPatienceMessage] = React.useState(false);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout;
    
    if (open) {
      setShowPatienceMessage(false);
      timeout = setTimeout(() => {
        setShowPatienceMessage(true);
      }, 15000); // 15 seconds
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [open]);

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <Backdrop
        sx={(theme) => ({ 
          color: theme.palette.text.primary, 
          zIndex: theme.zIndex.drawer + 1 
        })}
        open={open}
      >
        <Card variant="outlined">
          <Stack direction="column" sx={{ justifyContent: "center", alignItems: "center", gap: 4, maxWidth: 450 }}>
            <Typography sx={{ textAlign: 'center' }}>
              {showAgreementDetails ? 
                'Loading your Tariffs...' : 
                `Loading your ${meterType === 1 ? 'electricity' : meterType === 2 ? 'gas' : 'energy'} data...`
              }
            </Typography>
            {showPatienceMessage && (
              <Typography variant="h6" sx={{ textAlign: 'center', mt: -2}}>
                This may take a few minutes. Please wait and don't refresh the page.
              </Typography>
            )}        
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress variant='determinate' color="inherit" value={progress} size={80}/>
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h5"
                  component="div"
                >{`${Math.round(progress)}%`}</Typography>
              </Box>
            </Box>
            {showAgreementDetails && tariffName && agreementPeriod ? (
              <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
                <Typography variant="body1">
                  {tariffName}
                </Typography>
                <Typography variant="body1">
                  {agreementPeriod}
                </Typography>
              </Stack>
            ) : (
              <Typography sx={{ textAlign: 'center' }}>Loading {loadedTo}</Typography>
            )}
          </Stack>
        </Card>
      </Backdrop>
    </AppTheme>
  );
}